import { useMemo, useRef } from "react";
import type { Budget, CategoryTotals } from "../../../../types/budget/budget";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import {
  useBudgetMeta,
  useBudgetServicesState,
  useBudgetSuppliersState,
  useBudgetTotals,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { shouldUsePreservedTotals } from "../../../../utils/preserved-totals";
import { hasServicesInVersion } from "../../../../utils/budget-stage-visibility";
import { defineTotals } from "./methods";

export function useCategoryTotals(
  supplierId: string,
  type: keyof typeof BudgetItemTypeEnum
) {
  const { activeCalcOption, detailedBudget, versionInfo } = useBudgetMeta();
  const totalsSlice = useBudgetTotals();
  const { budgetServices } = useBudgetServicesState();
  const { supplierList, addedItemTypeList } = useBudgetSuppliersState();
  const { tabActive } = useProject();

  // Preserva os últimos totais válidos para evitar zeragem ao trocar fornecedor.
  // O valor guardado pertence a UMA identidade (fornecedor + categoria + forma
  // de cobrança + versão): quando ela muda — mover a categoria de bloco,
  // transferir para outro fornecedor, trocar o fornecedor da linha — o valor
  // antigo deixa de valer e precisa ser descartado, senão o header continua
  // exibindo o total de uma categoria que já saiu dali.
  const previousTotalsRef = useRef<{
    calcTotalsSource: CategoryTotals;
    calcTotalsCompare: CategoryTotals;
  } | null>(null);
  const identityRef = useRef<string>("");
  const identity = `${supplierId}|${type}|${activeCalcOption}|${
    versionInfo?.version ?? 0
  }|${tabActive}`;
  if (identityRef.current !== identity) {
    identityRef.current = identity;
    previousTotalsRef.current = null;
  }

  const budgetForTotals = useMemo(
    () =>
      ({
        budgetValuesTotal: totalsSlice.budgetValuesTotal,
        negociationValuesTotal: totalsSlice.negociationValuesTotal,
        closingValuesTotal: totalsSlice.closingValuesTotal,
        budgetServices,
        supplierList,
        addedItemTypeList,
      } as unknown as Budget),
    [
      totalsSlice.budgetValuesTotal,
      totalsSlice.negociationValuesTotal,
      totalsSlice.closingValuesTotal,
      budgetServices,
      supplierList,
      addedItemTypeList,
    ]
  );

  return useMemo(() => {
    const newTotals = defineTotals(
      activeCalcOption,
      detailedBudget,
      tabActive,
      budgetForTotals,
      supplierId,
      type,
      versionInfo
    );

    // Mesma regra do header de fornecedor: um zero só é transitório enquanto
    // a categoria ainda tiver serviço de onde o total possa vir. Categoria que
    // perdeu o último serviço para uma transferência tem zero REAL, e segurar
    // o valor guardado deixava o header exibindo o que já saiu dali.
    const preserve = shouldUsePreservedTotals({
      currentTotal: newTotals.calcTotalsSource.totals?.totalInBRL ?? 0,
      ownsContent: hasServicesInVersion(
        budgetServices,
        supplierId,
        activeCalcOption,
        versionInfo?.version ?? 0,
        type,
        versionInfo?.status
      ),
      hasPreserved: previousTotalsRef.current != null,
    });

    if (preserve) return previousTotalsRef.current!;

    previousTotalsRef.current =
      newTotals.calcTotalsSource.totals?.totalInBRL > 0 ? newTotals : null;
    return newTotals;
  }, [
    activeCalcOption,
    detailedBudget,
    tabActive,
    budgetForTotals,
    budgetServices,
    type,
    versionInfo,
    // `supplierId` PRECISA estar aqui: sem ele o memo devolvia o total do
    // fornecedor anterior depois de uma transferência de categoria. A
    // piscada que a exclusão evitava é tratada pelo ref acima.
    supplierId,
    identity,
  ]);
}
